import { HubConnection } from '@microsoft/signalr';
import { AppState } from '../types';

// API Base URL - Not used when SignalR is disabled
// const getApiBaseUrl = (): string => {
//   const isLocalhost = window.location.hostname === 'localhost' || 
//                      window.location.hostname === '127.0.0.1' ||
//                      window.location.hostname.startsWith('192.168.') ||
//                      window.location.hostname.startsWith('10.');
//   
//   if (isLocalhost) {
//     const currentHost = window.location.hostname;
//     return `http://${currentHost}:7071/api`;
//   }
//   return `${window.location.protocol}//${window.location.host}/api`;
// };

// const apiBaseUrl = getApiBaseUrl();

type SignalRConnectionState = 'Disconnected' | 'Connecting' | 'Connected' | 'Reconnecting' | 'Unavailable';

interface SignalREventHandlers {
  onTableUpdate: (appState: AppState) => void;
  onConnectionStateChanged: (state: SignalRConnectionState) => void;
  onError: (error: string) => void;
}

class SignalRService {
  private connection: HubConnection | null = null;
  private connectionState: SignalRConnectionState = 'Disconnected';
  private eventHandlers: Partial<SignalREventHandlers> = {};
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private initialized = false;
  private isConnecting = false;
  private lastDisconnectTime = 0;
  private rapidDisconnectCount = 0;
  private readonly RAPID_DISCONNECT_THRESHOLD = 5; // Disable after 3 rapid disconnects
  private readonly RAPID_DISCONNECT_WINDOW = 10000; // 10 seconds

  // API Configuration - Dynamisch für externe Geräte
  private getApiBaseUrl(): string {
    if (process.env.NODE_ENV === 'development') {
      const currentHost = window.location.hostname;
      return `http://${currentHost}:7071/api`;
    }
    return '/api';
  }

  // Event Handler registrieren
  onTableUpdate(handler: (appState: AppState) => void) {
    this.eventHandlers.onTableUpdate = handler;
  }

  onConnectionStateChanged(handler: (state: SignalRConnectionState) => void) {
    this.eventHandlers.onConnectionStateChanged = handler;
  }

  onError(handler: (error: string) => void) {
    this.eventHandlers.onError = handler;
  }

  // SignalR Verbindung initialisieren
  async initialize(): Promise<void> {
    // IMMEDIATE CIRCUIT BREAKER: Force disable SignalR due to persistent connection issues
    console.log('🚨 SignalR: CIRCUIT BREAKER ACTIVATED - Persistent connection issues detected');
    console.log('🔧 SignalR: Forcing permanent disable to prevent console spam');
    console.log('🔧 SignalR: App will use 5-second polling for stable operation');
    this.rapidDisconnectCount = this.RAPID_DISCONNECT_THRESHOLD + 1; // Force circuit breaker
    this.updateConnectionState('Unavailable');
    return;
  }

  private setupEventHandlers() {
    // SignalR disabled - no event handlers needed
    return;
  }

  private updateConnectionState(state: SignalRConnectionState) {
    this.connectionState = state;
    if (this.eventHandlers.onConnectionStateChanged) {
      this.eventHandlers.onConnectionStateChanged(state);
    }
  }

  private notifyError(error: string) {
    if (this.eventHandlers.onError) {
      this.eventHandlers.onError(error);
    }
  }

  // Verbindung manuell trennen
  async disconnect(): Promise<void> {
    console.log('🔌 SignalR: Trenne Verbindung...');
    
    this.initialized = false;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.log('ℹ️ SignalR: Disconnect Fehler ignoriert:', error);
      }
      this.connection = null;
    }
    
    this.updateConnectionState('Disconnected');
    console.log('✅ SignalR: Verbindung getrennt');
  }

  // Status abfragen
  getConnectionState(): SignalRConnectionState {
    return this.connectionState;
  }

  isConnected(): boolean {
    return this.connectionState === 'Connected';
  }

  // Health Check
  getHealthInfo() {
    return {
      state: this.connectionState,
      reconnectAttempts: this.reconnectAttempts,
      hasConnection: this.connection !== null,
      connectionId: this.connection?.connectionId || null
    };
  }
}

// Singleton Instance
export const signalRService = new SignalRService();